import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Package, Tags, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';

// Klucze grup kategorii znane workerowi (CATEGORY_GROUPS w products-store.ts).
// Trzymane tu jako podpowiedzi do pola mapowania — worker i tak akceptuje dowolny
// string, ale datalist chroni przed literówką w nazwie grupy.
const CATEGORY_GROUPS: { key: string; label: string }[] = [
	{ key: 'products_tv_audio_rtv', label: 'TV, Audio i RTV' },
	{ key: 'products_smartfony', label: 'Smartfony' },
	{ key: 'products_laptopy_sluchawki', label: 'Laptopy i słuchawki' },
	{ key: 'products_gaming', label: 'Gaming' },
	{ key: 'products_tablety', label: 'Tablety' },
	{ key: 'products_smartwatche', label: 'Smartwatche' },
	{ key: 'products_foto_kamery', label: 'Foto i kamery' },
	{ key: 'products_agd', label: 'AGD' },
];

const GROUP_LABELS = new Map(CATEGORY_GROUPS.map((g) => [g.key, g.label]));

export default function TagsPage() {
	const queryClient = useQueryClient();
	const [tag, setTag] = useState('');
	const [category, setCategory] = useState('');

	const tagsQuery = useQuery({
		queryKey: ['products-tags'],
		queryFn: api.products.tags,
	});

	const saveTagMutation = useMutation({
		mutationFn: ({ nextTag, nextCategory }: { nextTag: string; nextCategory: string }) =>
			api.products.setTag(nextTag, nextCategory),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['products-tags'] });
			setTag('');
			setCategory('');
		},
	});

	// Usunięcie mapowania = zapis z pustą kategorią (worker kasuje wpis).
	const removeTagMutation = useMutation({
		mutationFn: (nextTag: string) => api.products.setTag(nextTag, ''),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['products-tags'] });
		},
	});

	const tagEntries = useMemo(() => Object.entries(tagsQuery.data ?? {}), [tagsQuery.data]);

	return (
		<div className="flex flex-col gap-6 p-6">
			<div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
				<div>
					<div className="mb-2 flex items-center gap-2 text-sm font-medium text-muted-foreground">
						<Tags className="size-4" />
						Feed produktów
					</div>
					<h1 className="text-2xl font-semibold">Mapowanie tagów</h1>
					<p className="mt-1 text-sm text-muted-foreground">Przypisz tag artykułu do grupy kategorii używanej przez rekomendacje.</p>
				</div>

				<Link
					to="/feed"
					className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium transition hover:bg-accent"
				>
					<Package className="size-4" />
					Edycja produktów
				</Link>
			</div>

			<div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
				<section className="h-fit rounded-xl border border-border bg-card p-4 shadow-sm">
					<h2 className="text-lg font-semibold">Nowe mapowanie</h2>
					<p className="mt-1 text-sm text-muted-foreground">Pusta grupa kategorii usuwa mapowanie tagu.</p>

					<div className="mt-4 space-y-2">
						<input
							value={tag}
							onChange={(e) => setTag(e.target.value)}
							placeholder="Tag (np. samsung)"
							className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none"
						/>
						<input
							value={category}
							onChange={(e) => setCategory(e.target.value)}
							placeholder="Grupa kategorii"
							list="category-groups"
							className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none"
						/>
						<datalist id="category-groups">
							{CATEGORY_GROUPS.map((g) => (
								<option key={g.key} value={g.key}>
									{g.label}
								</option>
							))}
						</datalist>
						<button
							type="button"
							onClick={() => tag.trim() && saveTagMutation.mutate({ nextTag: tag.trim(), nextCategory: category.trim() })}
							className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
							disabled={saveTagMutation.isPending}
						>
							{saveTagMutation.isPending ? <Loader2 className="mr-2 inline size-4 animate-spin" /> : null}
							Zapisz mapowanie
						</button>
					</div>
				</section>

				<section className="rounded-xl border border-border bg-card p-4 shadow-sm">
					<h2 className="text-lg font-semibold">Aktualne mapowania</h2>
					<p className="mt-1 text-sm text-muted-foreground">Kliknij wpis, aby wczytać go do edycji.</p>

					<div className="mt-4 space-y-2">
						{tagsQuery.isLoading ? (
							<div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
								<Loader2 className="mr-2 size-4 animate-spin" />
								Ładowanie mapowań…
							</div>
						) : tagEntries.length ? (
							tagEntries.map(([key, value]) => (
								<div key={key} className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm">
									<button
										type="button"
										onClick={() => {
											setTag(key);
											setCategory(value);
										}}
										className="min-w-0 flex-1 text-left"
									>
										<span className="font-medium">{key}</span>{' '}
										<span className="text-muted-foreground">→ {(GROUP_LABELS.get(value) ?? value) || '—'}</span>
									</button>
									<button
										type="button"
										onClick={() => removeTagMutation.mutate(key)}
										className="inline-flex shrink-0 items-center justify-center rounded-md border border-border p-1.5 transition hover:bg-accent"
										disabled={removeTagMutation.isPending}
										aria-label={`Usuń mapowanie ${key}`}
									>
										<Trash2 className="size-4" />
									</button>
								</div>
							))
						) : (
							<p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
								Brak mapowań tagów.
							</p>
						)}
					</div>
				</section>
			</div>
		</div>
	);
}
